// WHAT IS THIS FILE? — the two normalisations every downstream comparison depends on.
//
// 1. `stripFeSuffix`  — TRAP 1. Every FE series name ends " in GB" or " TR in GB". Nothing
//    matches until that is gone.
// 2. `normaliseKey`   — the case/punctuation-blind form used to compare an FE name against a
//    `ba_cbam_funds` row. TRAP 6: FE strips share classes INCONSISTENTLY between pulls, and the
//    DB stores names in three different shapes ("Manager : Fund : Class", "Manager Fund Class",
//    and a bare abbreviation). Normalising both sides is the only way those ever meet.
//
// ⚠️ `normaliseKey` deliberately does NOT do stemming, token-subset matching, or edit distance.
// Everything built on it is EXACT-after-normalisation. TRAP 7 is why: `ba_trades` holds an orphan
// 'Fidelity : Japan : W Acc' with a NULL fund_id. Any matcher loose enough to bind that to
// "Fidelity Index Japan" — a different fund, separately held — mints a phantom position with a
// fabricated return attached to the wrong ISIN. An unmatched holding is a warning a human can
// resolve; a wrongly matched holding is a number nobody can see is wrong.

/** Removes the FE locale suffix. Returns the stripped name and whether it was the TR variant. */
export function stripFeSuffix(rawName: string): { name: string; totalReturn: boolean } {
  const name = rawName.trim();
  const tr = /\s+TR\s+in\s+GB\s*$/i;
  if (tr.test(name)) return { name: name.replace(tr, "").trim(), totalReturn: true };
  const plain = /\s+in\s+GB\s*$/i;
  if (plain.test(name)) return { name: name.replace(plain, "").trim(), totalReturn: false };
  return { name, totalReturn: false };
}

/**
 * Fold a fund/series name to a comparison key.
 *
 * Rules, applied in order, and each one earns its place against a real observed pair:
 *   - lowercase                       "BlackRock" vs "Blackrock" (bm_fund_houses casing)
 *   - drop full stops                 "Vanguard U.S. Investment Grade" vs "Vanguard US ..."
 *   - ` : ` separators -> space       "Fidelity : Index US : P" vs "Fidelity Index US P"
 *   - brackets -> space               "Invesco Money (UK) No Trail Acc" vs the DB's same
 *   - hyphens/dashes/slashes -> space "Asset-Backed" vs "Asset Backed", "TIPS 0-5" vs "TIPS 0 5"
 *   - collapse whitespace
 * `&`, `%`, `$` and digits survive — "L&G", "20%", "$ TIPS" are all load-bearing.
 */
export function normaliseKey(s: string | null | undefined): string {
  if (!s) return "";
  return s
    .toLowerCase()
    .replace(/\./g, "")
    .replace(/[‘’“”]/g, "'")
    .replace(/[:;,()[\]{}]/g, " ")
    .replace(/[-‐-―−/\\]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Join name parts, dropping a repeated leading manager token.
 *
 * `ba_cbam_funds` is inconsistent about whether `fund_name` already carries the manager:
 * `fund_manager='Fidelity', fund_name='Fidelity Index Japan'` but
 * `fund_manager='Man', fund_name='Sterling Corporate Bond Fund'`. Concatenating blindly produces
 * "fidelity fidelity index japan", which matches nothing.
 */
export function joinNameParts(...parts: (string | null | undefined)[]): string {
  const out: string[] = [];
  for (const part of parts) {
    const k = normaliseKey(part);
    if (!k) continue;
    const joined = out.join(" ");
    if (!joined) {
      out.push(k);
      continue;
    }
    // "fidelity" + "fidelity index japan" -> the later part subsumes the earlier one
    if (k === joined || k.startsWith(`${joined} `)) {
      out.length = 0;
      out.push(k);
      continue;
    }
    // already ends with this part ("... p acc" + "P Acc")
    if (joined.endsWith(` ${k}`)) continue;
    out.push(k);
  }
  return out.join(" ").trim();
}

/**
 * Parse an FE value field.
 *
 * ⚠️ TRAP 5. "IA Not yet assigned TR in GB" ships with NO value — in some exports the field is
 * absent entirely (the record has one field), in others it is the empty string. Neither is zero.
 * `Number("")` is 0 and `parseFloat(undefined as never)` is NaN; both are wrong answers dressed
 * as data. This returns null and lets the caller decide whether null is fatal for that row class.
 */
export function parseFeValue(raw: string | null | undefined): {
  value: number | null;
  unparseable: boolean;
} {
  if (raw === null || raw === undefined) return { value: null, unparseable: false };
  const cleaned = raw.trim().replace(/,/g, "").replace(/%$/, "").trim();
  if (cleaned === "" || cleaned === "-" || cleaned.toLowerCase() === "n/a") {
    return { value: null, unparseable: false };
  }
  const n = Number(cleaned);
  if (!Number.isFinite(n)) return { value: null, unparseable: true };
  return { value: n, unparseable: false };
}
